import React, { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScopeRequestContext } from '../../providers/ScopeRequestContext';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import MessagePanel from '../../common/components/MessagePanel/MessagePanel';
import { EventPayload } from '../../events';
import { Provider } from '../../config';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { ErrorType, complianceErrors, getErrorType } from '../../utils/errors';

function ErrorScreen(): JSX.Element {
  const { getEventPayload: getSipEventPayload } = useContext(ScopeRequestContext);
  const { provider, flowFailed, getOptionalFlowExecutionConfig } = useContext(FlowConfigContext);
  const navigate = useNavigate();
  const customErrorCode = getOptionalFlowExecutionConfig('errorCode');
  const location = useLocation();
  const errorCode: string = location.state?.errorCode || undefined;
  const prevPath: string = location.state?.prevPath || undefined;
  const errorType = getErrorType(errorCode);

  const handleConfirm = useCallback(() => {
    if (prevPath) {
      navigate(prevPath);
      return;
    }
    // If the current provider is SIP, then get the error payload form the SR context, which will include the SR id
    const eventPayload: EventPayload = provider === Provider.SIP ? getSipEventPayload() : { provider };
    flowFailed(eventPayload);
  }, [flowFailed, getSipEventPayload, navigate, prevPath, provider]);

  // custom error codes are defined in gateway-client-core, and if we want to show custom messages
  // the error code should be added to the internationalisation files
  const customErrorPath = `screens.error.${customErrorCode}`;
  return (
    <>
      {errorType === ErrorType.RateLimited ? (
        <MessagePanel
          title="screens.error.title_rate_limited"
          description="screens.error.description_rate_limited"
          buttonLabel="screens.error.closeButtonLabel"
          dataTestId="ERROR_SCREEN"
          onConfirm={handleConfirm}
          showSupportLink={false}
        />
      ) : complianceErrors.includes(errorType) ? (
        <MessagePanel
          title="screens.error.title_compliance_store_check_failed"
          description="screens.error.description_compliance_store_check_failed"
          buttonLabel="screens.error.closeButtonLabel"
          dataTestId="ERROR_SCREEN"
          onConfirm={handleConfirm}
          showSupportLink={false}
        />
      ) : (
        <MessagePanel
          title={[`${customErrorPath}.title`, 'screens.error.title']}
          description={[`${customErrorPath}.description`, 'screens.error.description']}
          buttonLabel="screens.error.buttonLabel"
          dataTestId="ERROR_SCREEN"
          onConfirm={handleConfirm}
          showSupportLink={false}
        />
      )}
    </>
  );
}
export default withRouteAnalytics(ErrorScreen, Screens.ERROR);
